import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AtSign, RotateCcw, User, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAccount, useUpdateProfile } from "@/queries/account";
import { getNativePlatform } from "@/lib/runtime";
import { NativeSettingsSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";

const accountInputClassName =
  "mt-2 h-11 rounded-xl border-border/70 bg-muted/30 px-4 shadow-none placeholder:text-muted-foreground/70 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20";

export default function ProfilePanel() {
  const { data, isLoading } = useAccount();
  const update = useUpdateProfile();
  const { hash } = useLocation();
  const emailSectionRef = useRef(null);
  const [form, setForm] = useState(() => ({
    display_name: data?.display_name || "",
    email: data?.pending_email || data?.email || "",
  }));
  const [errors, setErrors] = useState({ display_name: "", email: "" });

  useEffect(() => {
    if (!data) return;
    setForm({
      display_name: data.display_name || "",
      email: data.pending_email || data.email || "",
    });
  }, [data]);

  useEffect(() => {
    if (hash !== "#email") return;
    emailSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  if (isLoading && getNativePlatform() === "android") {
    return <NativeSettingsSkeleton sections={2} />;
  }

  const savedProfile = {
    display_name: data?.display_name || "",
    email: data?.pending_email || data?.email || "",
  };

  const displayNameDirty = form.display_name !== savedProfile.display_name;
  const emailDirty = form.email !== savedProfile.email;

  const onChange = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validateProfilePayload = (payload) => {
    const nextErrors = { display_name: "", email: "" };

    if (Object.prototype.hasOwnProperty.call(payload, "display_name")) {
      const displayName = String(payload.display_name ?? "").trim();
      if (!displayName) {
        nextErrors.display_name = "Visningsnamn måste fyllas i.";
      }
    }

    if (Object.prototype.hasOwnProperty.call(payload, "email")) {
      const email = String(payload.email ?? "").trim();
      if (!email) {
        nextErrors.email = "E-postadress måste fyllas i.";
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        nextErrors.email = "Ange en giltig e-postadress.";
      }
    }

    setErrors((prev) => ({ ...prev, ...nextErrors }));
    return !nextErrors.display_name && !nextErrors.email;
  };

  const onSave = async (payload) => {
    const normalizedPayload = {
      ...payload,
      ...(Object.prototype.hasOwnProperty.call(payload, "display_name")
        ? { display_name: String(payload.display_name ?? "").trim() }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(payload, "email")
        ? { email: String(payload.email ?? "").trim() }
        : {}),
    };

    if (!validateProfilePayload(normalizedPayload)) {
      toast.error("Kontrollera formuläret och försök igen.");
      return;
    }

    const res = await toast.promise(update.mutateAsync(normalizedPayload), {
      loading: "Sparar…",
      success: "Profil uppdaterad",
      error: (e) => {
        if (normalizedPayload.email && e?.status === 422) {
          setErrors((prev) => ({
            ...prev,
            email: "Ange en giltig e-postadress.",
          }));
          return "Ange en giltig e-postadress.";
        }
        return e?.message || "Kunde inte spara";
      },
    });

    if (res?.email_verification_sent) {
      toast.message("Verifiering krävs", {
        description: "Vi har skickat en bekräftelselänk till din nya e-postadress.",
      });
    }
    if (res?.pending_email_cleared) {
      toast.message("Väntande e-postbyte borttaget", {
        description: "Din nuvarande e-postadress fortsätter att gälla.",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5 shrink-0 opacity-70" />
          <span>Profil</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="rounded-2xl border bg-card/60 p-4">
          <label className="flex items-center gap-2 text-sm font-medium">
            <UserRound className="h-4 w-4 text-muted-foreground" />
            Visningsnamn
          </label>
          <Input
            className={accountInputClassName}
            value={form.display_name}
            onChange={onChange("display_name")}
            placeholder="Hur ditt namn ska visas i appen"
            autoComplete="nickname"
          />
          {errors.display_name ? (
            <p className="mt-2 text-xs text-destructive">{errors.display_name}</p>
          ) : null}
          <p className="mt-2 text-xs text-muted-foreground">
            Syns i din profil och i delar av appen där ditt namn visas.
          </p>
          <div className="mt-3 flex justify-end gap-2">
            {displayNameDirty && (
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    display_name: savedProfile.display_name,
                  }))
                }
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Ångra
              </Button>
            )}
            <Button
              type="button"
              onClick={() => onSave({ display_name: form.display_name })}
              disabled={update.isPending || !displayNameDirty}
            >
              Spara visningsnamn
            </Button>
          </div>
        </div>

        <div ref={emailSectionRef} id="email" className="rounded-2xl border bg-card/60 p-4">
          <label className="flex items-center gap-2 text-sm font-medium">
            <AtSign className="h-4 w-4 text-muted-foreground" />
            E-postadress
          </label>
          <Input
            className={accountInputClassName}
            type="email"
            value={form.email}
            onChange={onChange("email")}
            placeholder="namn@epost.se"
            autoComplete="email"
            inputMode="email"
          />
          {errors.email ? (
            <p className="mt-2 text-xs text-destructive">{errors.email}</p>
          ) : null}
          {data?.pending_email ? (
            <div className="mt-3 rounded-xl border border-amber-300/70 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <p className="font-medium">Väntar på bekräftelse: {data.pending_email}</p>
              <p className="mt-1 text-amber-800/90">
                Tills den nya adressen bekräftas loggar du fortfarande in med {data.email}.
              </p>
            </div>
          ) : null}
          <p className="mt-2 text-xs text-muted-foreground">
            {data?.pending_email
              ? "Spara den nuvarande adressen igen om du vill avbryta bytet."
              : "Om du ändrar adressen skickas en ny verifieringslänk."}
          </p>
          <div className="mt-3 flex justify-end gap-2">
            {emailDirty && (
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    email: savedProfile.email,
                  }))
                }
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Ångra
              </Button>
            )}
            <Button
              type="button"
              onClick={() => onSave({ email: form.email })}
              disabled={update.isPending || !emailDirty}
            >
              Spara e-post
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
